import React, { useState } from 'react';
import { Database, FileText, Tag } from 'lucide-react';

const CentralizeSection = ({ isDarkMode }) => {
  const [activeTab, setActiveTab] = useState('centralize');

  const tabs = [
    {
      id: 'centralize',
      label: 'Centralize',
      icon: Database,
      content: {
        title: 'Centralize',
        description: 'We consolidate all **official** sources of government information—like press releases, voting records, and social media posts—across different websites and platforms into one place.'
      }
    },
    {
      id: 'summarize',
      label: 'Summarize',
      icon: FileText,
      content: {
        title: 'Summarize',
        description: 'We use AI to translate complex legislative language into plain language summaries that anyone can understand, with every summary linking back to its original source so you can always verify it for yourself.'
      }
    },
    {
      id: 'categorize',
      label: 'Categorize',
      icon: Tag,
      content: {
        title: 'Categorize',
        description: 'We organize everything by the issues that matter to you—education, healthcare, environment, public safety—so you can filter to what you care about and see exactly what\'s happening.'
      }
    }
  ];

  const activeTabData = tabs.find(tab => tab.id === activeTab);

  return (
    <section className={`${isDarkMode ? 'bg-gray-900' : 'bg-white'} py-16 md:py-20`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-[#3b82f6] mb-4">
            Centralize, Summarize, and Categorize
          </h2>
          <p className={`text-lg md:text-xl ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} max-w-3xl mx-auto`}>
            We consolidate all official sources of government information, we simplify them so anyone can understand, and we make it issue-focused.
          </p>
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 md:gap-8 mb-12">
          {/* Left Column - Tabs */}
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-md md:w-fit md:min-w-[250px] overflow-hidden`}>
            {tabs.map((tab, index) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <div key={tab.id}>
                  <button
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-6 transition-all duration-200 ${
                      isActive
                        ? 'bg-[#3b82f6] text-white'
                        : isDarkMode
                          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isActive
                        ? 'bg-blue-400'
                        : isDarkMode
                          ? 'bg-blue-900/30'
                        : 'bg-blue-50'
                    }`}>
                      <Icon className={`w-5 h-5 ${
                        isActive
                          ? 'text-white'
                          : 'text-[#3b82f6]'
                      }`} />
                    </div>
                    <span className={`font-medium text-left whitespace-nowrap ${
                      isActive
                        ? 'text-white'
                        : isDarkMode
                          ? 'text-gray-300'
                        : 'text-gray-700'
                    }`}>{tab.label}</span>
                  </button>
                  {index < tabs.length - 1 && !isActive && (
                    <div className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}></div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Right Column - Content */}
          <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border p-10 md:p-12 shadow-md`}>
            {activeTabData && (
              <>
                <h3 className={`text-2xl md:text-3xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'} mb-4`}>
                  {activeTabData.content.title}
                </h3>
                <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} leading-relaxed`}>
                  {activeTabData.content.description.split('**').map((part, index) => 
                    index % 2 === 1 ? (
                      <strong key={index}>{part}</strong>
                    ) : (
                      <span key={index}>{part}</span>
                    )
                  )}
                </p>
              </>
            )}
          </div>
        </div>

        {/* Bottom Text */}
        <div className="text-center">
          <p className={`text-lg md:text-xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'} max-w-4xl mx-auto`}>
            In a few clicks, you should be able to know who your elected officials are, what they're doing in government, and how it relates to the issues that you care about.
          </p>
        </div>
      </div>
    </section>
  );
};

export default CentralizeSection;
